import { mergeRefs } from "@react-aria/utils";
import React from "react";
import {
  ButtonContext,
  Group,
  InputContext,
  OverlayTriggerStateContext,
  PopoverContext,
  Provider,
  SelectContext,
  type SlotProps,
  useSlottedContext,
} from "react-aria-components";
import { useFieldErrorContext } from "../../context.tsx";
import type { LoadingState } from "../Spinner.tsx";
import { ControlledSpinner } from "../internal/ControlledSpinner.tsx";
import { useSuccessSpinnerState } from "../internal/useSuccessSpinnerState.ts";
import type { IconType } from "../types.ts";
import { AvatarContext } from "./AvatarContext.ts";
import ErrorIcon from "./ErrorIcon.tsx";
import * as styles from "./InputBuilder.css.ts";

type InputSize = keyof typeof styles.container.classNames.variants.size;

export interface InputBuilderProps {
  /** Left slot content - can be an icon, Avatar, or interactive element like a dropdown. */
  left?: React.ReactNode | IconType;
  /** Size of the input. */
  size?: InputSize;
  /** Static text prefix displayed before the input (e.g., currency symbol). */
  leadingText?: string;
  /** Loading state indicator for async operations. */
  loadingState?: LoadingState;
}

export interface RenderState {
  isDisabled: boolean;
  isError: boolean;
  isOpen: boolean;
  loadingState?: LoadingState;
  loadingRenderState: LoadingState | undefined;
  loadingStartAnimation: boolean;
  loadingOnSuccessAnimationEnd: () => void;
}

interface BuilderProps extends InputBuilderProps, SlotProps {
  children: React.ReactNode;

  /** Right slot content - typically ChevronButton, ClearButton, or custom dropdowns. If function, receives render state. */
  right?: React.ReactNode | ((props: RenderState) => React.ReactNode);

  isDisabled?: boolean;
  ref?: React.Ref<HTMLDivElement>;
}

export const InputBuilder: React.FC<BuilderProps> = ({
  slot,
  left,
  children,
  right,
  size = "m",
  loadingState,
  leadingText,
  isDisabled: isDisabledProp,
  ref: externalRef,
}) => {
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const existingInput = useSlottedContext(InputContext, slot);
  const existingSelect = useSlottedContext(SelectContext, slot);
  const existingButton = useSlottedContext(ButtonContext, slot);
  const existingPopover = useSlottedContext(PopoverContext, slot);
  const validation = useFieldErrorContext();

  const { renderState, startAnimation, onSuccessAnimationEnd } =
    useSuccessSpinnerState(loadingState);
  const isError = validation?.isInvalid === true || loadingState === "error";
  const isOpen = React.use(OverlayTriggerStateContext)?.isOpen === true;
  const isDisabled =
    isDisabledProp ??
    (existingInput?.disabled === true || existingButton?.isDisabled === true);
  const rightContent = React.useMemo(() => {
    if (typeof right === "function") {
      return right({
        isDisabled,
        isError,
        isOpen,
        loadingState,
        loadingRenderState: renderState,
        loadingStartAnimation: startAnimation,
        loadingOnSuccessAnimationEnd: onSuccessAnimationEnd,
      });
    }
    return right;
  }, [
    isDisabled,
    isError,
    isOpen,
    loadingState,
    onSuccessAnimationEnd,
    renderState,
    right,
    startAnimation,
  ]);

  return (
    <Group
      ref={mergeRefs(externalRef, triggerRef)}
      className={styles.container({ size, isError })}
      isDisabled={isDisabled}
    >
      <Provider
        values={[
          [
            AvatarContext,
            {
              size: "xxs",
              isInteractive: false,
              tooltip: false,
              insideInputBuilder: true,
            },
          ],
        ]}
      >
        {typeof left === "function"
          ? React.createElement(left, { className: styles.leftIcon })
          : left}
      </Provider>
      {leadingText != null && (
        <span aria-hidden className={styles.leadingText({ size })}>
          {leadingText}
        </span>
      )}
      <div role="presentation" className={styles.inputWrapper}>
        <Provider
          values={[
            [
              InputContext,
              {
                ...existingInput,
                className: styles.input({ size }),
              },
            ],
            [
              SelectContext,
              {
                ...existingSelect,
                className: styles.select,
              },
            ],
            [
              ButtonContext,
              {
                ...existingButton,
                className: styles.input({ size }),
              },
            ],
            [
              PopoverContext,
              {
                ...existingPopover,
                triggerRef,
              },
            ],
          ]}
        >
          {children}
        </Provider>
      </div>
      <Group role="presentation" className={styles.buttonGroup}>
        {loadingState != null && typeof right !== "function" && (
          <ControlledSpinner
            renderState={renderState}
            startAnimation={startAnimation}
            onSuccessAnimationEnd={onSuccessAnimationEnd}
            size="s"
            color="royalBlue"
          />
        )}
        {isError && loadingState !== "error" && typeof right !== "function" && (
          <ErrorIcon />
        )}
        {rightContent}
      </Group>
    </Group>
  );
};
